/**
 * Quotations React Query Hooks
 * Hooks for Purchase and Sales Quotations management
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createQueryKeys } from './queryKeys';
import * as quotationsService from '@/services/quotationsService';
import type {
  PurchaseQuotation,
  SalesQuotation,
  Quotation,
  PurchaseQuotationFilter,
  SalesQuotationFilter,
  PurchaseQuotationListResponse,
  SalesQuotationListResponse,
  CreatePurchaseQuotationRequest,
  CreateSalesQuotationRequest,
  UpdatePurchaseQuotationRequest,
  UpdateSalesQuotationRequest,
} from '@/types/quotations';
import type {
  PartsByVendorResponse,
  VendorsByPartResponse,
  BestPriceResponse,
} from '@/services/quotationsService';

// Query keys for purchase quotations
export const purchaseQuotationKeys = createQueryKeys<PurchaseQuotationFilter>('purchaseQuotations');

// Query keys for sales quotations
export const salesQuotationKeys = createQueryKeys<SalesQuotationFilter>('salesQuotations');

// ==================== Purchase Quotations ====================

/**
 * Hook to list purchase quotations
 */
export function usePurchaseQuotations(filter?: PurchaseQuotationFilter) {
  return useQuery<PurchaseQuotationListResponse, Error>(
    purchaseQuotationKeys.list(filter),
    () => quotationsService.listPurchaseQuotations(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to get a single purchase quotation
 */
export function usePurchaseQuotation(id: string | undefined) {
  return useQuery<PurchaseQuotation, Error>(
    purchaseQuotationKeys.detail(id || ''),
    () => quotationsService.getPurchaseQuotation(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a purchase quotation
 */
export function useCreatePurchaseQuotation() {
  const queryClient = useQueryClient();

  return useMutation<PurchaseQuotation, Error, CreatePurchaseQuotationRequest>(
    (data) => quotationsService.createPurchaseQuotation(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(purchaseQuotationKeys.lists());
      },
    }
  );
}

/**
 * Hook to update a purchase quotation
 */
export function useUpdatePurchaseQuotation() {
  const queryClient = useQueryClient();

  return useMutation<
    PurchaseQuotation,
    Error,
    { id: string; data: UpdatePurchaseQuotationRequest }
  >(
    ({ id, data }) => quotationsService.updatePurchaseQuotation(id, data),
    {
      onSuccess: (updatedQuotation) => {
        queryClient.invalidateQueries(purchaseQuotationKeys.lists());
        queryClient.setQueryData(
          purchaseQuotationKeys.detail(updatedQuotation.quotation_id),
          updatedQuotation
        );
      },
    }
  );
}

/**
 * Hook to delete a purchase quotation
 */
export function useDeletePurchaseQuotation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    (id) => quotationsService.deletePurchaseQuotation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(purchaseQuotationKeys.lists());
      },
    }
  );
}

/**
 * Hook to get purchase quotations by vendor code
 */
export function usePurchaseQuotationsByVendor(vendorCode: string | undefined) {
  return useQuery<PurchaseQuotation[], Error>(
    ['purchaseQuotations', 'byVendor', vendorCode],
    () => quotationsService.getPurchaseQuotationsByVendor(vendorCode!),
    {
      enabled: !!vendorCode,
      staleTime: 30000,
    }
  );
}

// ==================== Sales Quotations ====================

/**
 * Hook to list sales quotations
 */
export function useSalesQuotations(filter?: SalesQuotationFilter) {
  return useQuery<SalesQuotationListResponse, Error>(
    salesQuotationKeys.list(filter),
    () => quotationsService.listSalesQuotations(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to get a single sales quotation
 */
export function useSalesQuotation(id: string | undefined) {
  return useQuery<SalesQuotation, Error>(
    salesQuotationKeys.detail(id || ''),
    () => quotationsService.getSalesQuotation(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a sales quotation
 */
export function useCreateSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation<SalesQuotation, Error, CreateSalesQuotationRequest>(
    (data) => quotationsService.createSalesQuotation(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(salesQuotationKeys.lists());
      },
    }
  );
}

/**
 * Hook to update a sales quotation
 */
export function useUpdateSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation<
    SalesQuotation,
    Error,
    { id: string; data: UpdateSalesQuotationRequest }
  >(
    ({ id, data }) => quotationsService.updateSalesQuotation(id, data),
    {
      onSuccess: (updatedQuotation) => {
        queryClient.invalidateQueries(salesQuotationKeys.lists());
        queryClient.setQueryData(
          salesQuotationKeys.detail(updatedQuotation.quotation_id),
          updatedQuotation
        );
      },
    }
  );
}

/**
 * Hook to delete a sales quotation
 */
export function useDeleteSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    (id) => quotationsService.deleteSalesQuotation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(salesQuotationKeys.lists());
      },
    }
  );
}

/**
 * Hook to get sales quotations by customer code
 */
export function useSalesQuotationsByCustomer(customerCode: string | undefined) {
  return useQuery<SalesQuotation[], Error>(
    ['salesQuotations', 'byCustomer', customerCode],
    () => quotationsService.getSalesQuotationsByCustomer(customerCode!),
    {
      enabled: !!customerCode,
      staleTime: 30000,
    }
  );
}

// ==================== Cross-type Queries ====================

/**
 * Hook to get quotations by part code (across both types)
 */
export function useQuotationsByPartCode(partCode: string | undefined) {
  return useQuery<Quotation[], Error>(
    ['quotations', 'byPartCode', partCode],
    () => quotationsService.getQuotationsByPartCode(partCode!),
    {
      enabled: !!partCode,
      staleTime: 30000,
    }
  );
}

// ==================== Firestore-based Purchase Quotation Queries ====================
// These hooks use the new Firestore endpoints for efficient vendor/part queries

/**
 * Query keys for Firestore-based purchase quotation queries
 */
export const purchaseQuotationIndexKeys = {
  all: ['purchaseQuotationIndex'] as const,
  partsByVendor: (vendorCode: string) => [...purchaseQuotationIndexKeys.all, 'partsByVendor', vendorCode] as const,
  vendorsByPart: (partCode: string) => [...purchaseQuotationIndexKeys.all, 'vendorsByPart', partCode] as const,
  bestPrice: (vendorCode: string, partCode: string) => [...purchaseQuotationIndexKeys.all, 'bestPrice', vendorCode, partCode] as const,
};

/**
 * Hook to get all parts quoted by a specific vendor
 * This is the key hook for PO part selection - only shows parts the vendor has quoted
 */
export function usePartsByVendor(vendorCode: string | undefined) {
  return useQuery<PartsByVendorResponse, Error>(
    purchaseQuotationIndexKeys.partsByVendor(vendorCode || ''),
    () => quotationsService.getPartsByVendor(vendorCode!),
    {
      enabled: !!vendorCode,
      staleTime: 60000, // Cache for 1 minute
    }
  );
}

/**
 * Hook to get all vendors that have quoted a specific part
 */
export function useVendorsByPart(partCode: string | undefined) {
  return useQuery<VendorsByPartResponse, Error>(
    purchaseQuotationIndexKeys.vendorsByPart(partCode || ''),
    () => quotationsService.getVendorsByPart(partCode!),
    {
      enabled: !!partCode,
      staleTime: 60000,
    }
  );
}

/**
 * Hook to get the best price for a specific vendor+part combination
 */
export function useBestPrice(vendorCode: string | undefined, partCode: string | undefined) {
  return useQuery<BestPriceResponse, Error>(
    purchaseQuotationIndexKeys.bestPrice(vendorCode || '', partCode || ''),
    () => quotationsService.getBestPrice(vendorCode!, partCode!),
    {
      enabled: !!vendorCode && !!partCode,
      staleTime: 60000,
    }
  );
}

/**
 * Hook to trigger sync from Google Sheets to Firestore (admin only)
 */
export function useSyncFromSheets() {
  const queryClient = useQueryClient();

  return useMutation<{ status: string }, Error, void>(
    () => quotationsService.syncFromSheets(),
    {
      onSuccess: () => {
        // Invalidate all index queries after sync
        queryClient.invalidateQueries(purchaseQuotationIndexKeys.all);
        queryClient.invalidateQueries(purchaseQuotationKeys.lists());
      },
    }
  );
}
